import { CalendarAdd, CalendarCheck } from '@griply/assets';
import { useCalendarTaskSync } from 'features/external-connections';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSyncMenu } from 'shared/components/ui/calendar-sync-menu';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Task } from 'shared/types/task';

export type TaskCalendarSyncButtonProps = {
  task: Task;
};

// Unlike the calendar event card's badge, this one is always visible while the
// task has a calendar it could sync to — there is no hover surface to reveal it.
export const TaskCalendarSyncButton: React.FC<TaskCalendarSyncButtonProps> = ({
  task,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const tasks = useMemo(() => [task], [task]);
  const { states, onToggleSync, onToggleVisibility } =
    useCalendarTaskSync(tasks);

  const syncState = states.get(task.id);

  const handleToggleSync = () => onToggleSync(task, !!syncState?.isSynced);

  const handleToggleVisibility = () =>
    onToggleVisibility(task, !!syncState?.isHidden);

  if (!syncState) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={buttonRef}
        icon={syncState.isSynced ? CalendarCheck : CalendarAdd}
        color={IconButtonColors.Default}
        strokeWidth={1.5}
        size={IconButtonSizes.Medium}
        highlighted={syncState.isSynced}
        onClick={openMenu}
        aria-label={t('calendar.event-sync.aria-label')}
        preventDrag
      />

      {menuOpen && (
        <CalendarSyncMenu
          isSynced={syncState.isSynced}
          isHidden={syncState.isHidden}
          onToggleSync={handleToggleSync}
          onToggleVisibility={handleToggleVisibility}
          onClose={closeMenu}
          referenceElement={buttonRef}
        />
      )}
    </>
  );
};
